// Copyright ©️ 2026 eVolve MEP, LLC

/* oxlint-disable typescript/no-explicit-any */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type EvolveApiRoute,
  type EvolveApiResponse,
  type PageFetcher,
  type AdditionalRequestConfig,
  type TableViewType,
  useEvolveGet,
  isCanceledApiRequest,
} from '@evolvemep/api-ts';
import { Flex } from '@mantine/core';
import type {
  ColDef,
  ColumnVisibleEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IDetailCellRendererParams,
  IServerSideGetRowsParams,
  DefaultMenuItem,
  MenuItemDef,
  GetMainMenuItemsParams,
  StateUpdatedEvent,
} from 'ag-grid-enterprise';
import type { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { v4 as uuidv4 } from 'uuid';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import ResetIcon from 'assets/icons/icon-src/user-actions/reset.svg?raw';
import SortIcon from 'assets/icons/icon-src/user-actions/sort.svg?raw';
import { CellRenderer } from 'components/Mantine/CellRenderer';
import { useMantineNotifications } from 'components/Mantine/useMantineNotifications';
import { DetailGridRenderer } from 'helpers/ag-grid/DetailTables/DetailGridRenderer';
import type { DetailTable, detailTableSymbol } from 'helpers/ag-grid/DetailTables/detailTable';
import { ExportPaginatedGridButton } from 'helpers/ag-grid/Exports/ExportGridButton';
import type { GroupingSettings } from 'helpers/ag-grid/Grouping/createGridGrouping';
import { GroupRowRenderer } from 'helpers/ag-grid/Grouping/GroupRowRenderer';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';
import { SAVED_GRID_STATE_KEYS, TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { loadSavedGridState } from 'helpers/ag-grid/TableViewPanel/loadSavedGridState';
import { useInitialTableView } from 'helpers/ag-grid/TableViewPanel/useInitialTableView';
import { useTableViews } from 'helpers/ag-grid/TableViewPanel/useTableViews';
import { stringifyGridStateJson } from 'helpers/ag-grid/TableViewPanel/utils';
import { useSideBar } from 'helpers/ag-grid/useSideBar';
import type { KeysOfType } from 'helpers/helper-types';
import { isKeyOf } from 'helpers/isKeyOf';
import { isNil, isNotNil } from 'helpers/isNotNil';

import {
  baseColumnDef,
  baseGridDef,
  compactRowHeight,
  doNotSaveColumnIdentifier,
  menuOverrides,
} from './baseColumnDef';
import { convertAgGridRequestToParams } from './convertAgGridRequest';

type Props<
  TRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  TData extends EvolveApiResponse<'GET_PAGINATED', TRoute>['data'][number],
  TKey extends KeysOfType<TData, string>,
  DetailTitle extends string,
  /** The group data type for our grid, if any */
  GData extends { entireCount?: number },
  GKey extends KeysOfType<GData, string>,
> = {
  tableName: string;
  fetchPage: PageFetcher<TRoute>;
  /** Used to abort any in-progress fetch requests when those requests are no longer necessary. */
  abortAll: (reason: string) => void;
  /** The table view type ID for the table, used to enable saved table views. */
  tableViewType?: TableViewType;
  /** If true, shows the sidebar for column, filter, and table view management. */
  showSidebar?: boolean;
  /**
   * The columnDefs for this table.
   * Simply a pass-through to make spreading required props onto the table easier.
   */
  colDef: ColDef<TData>[];
  /** Unique ID key of each row. Value must be of type string. */
  rowId: TKey | ((data: TData) => string);
  detailTables?: readonly (DetailTable<any, any, TData, DetailTitle, any> & {
    [detailTableSymbol]: never;
  })[];
  /**
   * Any `context` to pass directly to the `AgGridReact` component. Make sure to memoize this.
   *
   * Note the typing is loose here because, currently, AG Grid doesn't actually make use of the context type.
   */
  context?: unknown;
  /** Server-side grouping settings. Make sure to memoize this! */
  grouping?: GroupingSettings<TRoute, GData, GKey>;
  /** Optional file name to use for exports. Defaults to `tableName` if not provided. */
  exportFileName?: string;
};

export type MidLevelRow<TData, DetailTitle extends string> = {
  parentRowData: TData | undefined;
} & Pick<DetailTable<any, any, TData, DetailTitle>, 'title' | 'icon'>;

/**
 * Used to generate all necessary props to pass into an `<AgGridReact />`
 */
export const useServerSideGrid = <
  TRoute extends EvolveApiRoute<'GET_PAGINATED'>,
  TData extends EvolveApiResponse<'GET_PAGINATED', TRoute>['data'][number],
  TKey extends KeysOfType<TData, string>,
  DetailTitle extends string = string,
  GData extends { entireCount?: number } = any,
  GKey extends KeysOfType<GData, string> = KeysOfType<GData, string>,
>({
  tableName,
  tableViewType,
  showSidebar = isNotNil(tableViewType),
  fetchPage,
  abortAll,
  /** Make sure to memoize this */
  grouping,
  colDef,
  rowId,
  detailTables: originalDetailTables,
  context,
  exportFileName,
}: Props<TRoute, TData, TKey, DetailTitle, GData, GKey>) => {
  /**
   * For non-grouped grid, this is always TData.
   * For grouped grids, each top-level row might be either TData or GroupedDataType.
   * In those instances, you can check `__type` to determine which it is.
   */
  type PotentialRowType =
    | (TData & {
        __type?: never;
      })
    | (GData & {
        __type?: 'group';
      })
    | {
        __type: 'no-data';
        id: string;
      };

  const gridRef = useRef<AgGridReact<TData>>(null);
  const [gridApi, setGridApi] = useState<GridApi<TData> | null>(null);
  const { showError } = useMantineNotifications();
  // `detailTables` is usually defined in-line, meaning every re-render
  // causes `detailTables` to be recreated, causing the AG Grid props to be recreated.
  // We use `useState` here to prevent this domino effect from happening,
  // though it has the side effect of making editing the detail tables much harder.
  // Luckily, that use case doesn't exist yet.
  const [detailTables] = useState(originalDetailTables);
  const masterDetail = useMemo(() => isNotNil(detailTables), [detailTables]);
  const rowHeight = useMemo(() => (masterDetail ? compactRowHeight : undefined), [masterDetail]);
  const [totalRowCount, setTotalRowCount] = useState<number>();

  const abortRequests = useCallback(() => abortAll('Data source changed'), [abortAll]);
  // Abort all requests on unmount
  useEffect(() => () => abortAll('Server side grid unmounted'), [abortAll]);

  useEffect(() => {
    // Effects run after layout paint, so it's possible for the grid to already be destroyed by the time this effect runs.
    if (!gridApi || gridApi.isDestroyed()) return undefined;
    gridApi.addEventListener('filterModified', abortRequests);
    gridApi.addEventListener('sortChanged', abortRequests);
    return () => {
      if (!gridApi.isDestroyed()) {
        gridApi.removeEventListener('filterModified', abortRequests);
        gridApi.removeEventListener('sortChanged', abortRequests);
      }
    };
  }, [abortRequests, gridApi]);

  const setDataSource = useCallback(
    (api: GridApi<TData>) => {
      abortRequests();

      setTimeout(() => {
        if (api.isDestroyed()) return;
        // If the datasource itself changes,
        // the existing row count is no longer valid
        setTotalRowCount(undefined);
        api.setGridOption('serverSideDatasource', {
          getRows: async ({ request, success, fail, parentNode }: IServerSideGetRowsParams<PotentialRowType>) => {
            try {
              api.setGridOption('activeOverlay', undefined);
              api.setGridOption('loading', true);
              // If we're requesting fresh data, any previous selection is no longer valid
              if (request.startRow === 0) {
                api.deselectAll();
              }
              let data: PotentialRowType[], entireCount: number;
              if (
                grouping &&
                // groupKeys is empty when making the call to get the list of groups
                request.groupKeys.length === 0
              ) {
                data = (await grouping.fetchGroups(request)).map((d) => ({
                  ...d,
                  __type: 'group',
                }));
                entireCount = data.length;
              }
              // Either we're not using groups, or we're getting the data for a particular group
              else {
                // Get the proper params to pass to `fetchPage` for our particular group, if applicable
                const groupParams = (
                  grouping && parentNode.data?.__type === 'group'
                    ? (grouping.configMapper?.(parentNode.data, request) ?? {
                        [grouping.rowId]: (parentNode.data as GData)[grouping.rowId],
                      })
                    : {}
                ) as AdditionalRequestConfig<'GET_PAGINATED', TRoute>;

                const [a, b] = convertAgGridRequestToParams<TRoute>(request, groupParams);

                const res = await fetchPage(a, b).catch((err) => {
                  // Canceled requests were canceled on purpose,
                  // no need to show an error.
                  if (!isCanceledApiRequest(err) && String(err) !== 'Data source changed') {
                    showError(err, false);
                  }
                  throw err;
                });
                data = res.data as unknown as PotentialRowType[];
                entireCount = res.entireCount;

                if (grouping) {
                  if (parentNode.data?.__type === 'group') {
                    parentNode.setData({
                      ...parentNode.data,
                      // Update the parent group node with the updated `entireCount` from the BE
                      entireCount,
                    });
                  }
                  // If no data in the group, create a "nothing found" row to indicate to the user as such
                  if (entireCount === 0) {
                    data = [{ __type: 'no-data', id: uuidv4() }];
                    entireCount = 1;
                  }
                }
              }
              // If the Grid was unmounted during the API call,
              // no need to do anything
              if (!api.isDestroyed()) {
                success({
                  rowData: data,
                  rowCount: entireCount,
                });
                setTotalRowCount(entireCount);

                const noData = entireCount === 0 || data.every((d) => d.__type === 'group' && d.entireCount === 0);
                if (noData) {
                  api.setGridOption('activeOverlay', 'agNoRowsOverlay');
                } else {
                  api.setGridOption('activeOverlay', undefined);

                  if (grouping) {
                    data.forEach((row) => {
                      if (row.__type === 'group') {
                        const toExpand =
                          (typeof grouping.autoExpand === 'boolean'
                            ? grouping.autoExpand
                            : grouping.autoExpand?.(row)) ?? true;
                        api.getRowNode(row[grouping.rowId] as string)?.setExpanded(toExpand);
                      }
                    });
                  }
                }
              }
            } catch {
              fail();
            } finally {
              if (!api.isDestroyed()) api.setGridOption('loading', false);
            }
          },
        });
      });
    },
    // When fetchPage changes, so will setDataSource...
    [abortRequests, grouping, fetchPage, showError],
  );
  // ...and when setDataSource changes, we'll then re-set the datasource on the grid,
  // which will automatically cause it to re-fetch using the new fetchPage
  useEffect(() => {
    if (isNotNil(gridRef.current?.api)) {
      setDataSource(gridRef.current.api);
    }
  }, [setDataSource]);

  // Calculates the extra spacing needed to indent detail tables to look good
  const extraSpace = useMemo(() => {
    const dropdown = colDef.findIndex((c) => c.cellRenderer === 'agGroupCellRenderer');
    return colDef
      .slice(0, dropdown + 1)
      .filter((c) => !c.hide)
      .reduce((o, c) => Math.max(c.width ?? 0, 48) + o, 0);
  }, [colDef]);

  const { selectedTableViewId, selectedTableViewModified, recordAppliedTableView, recordGridState } = useTableViews(
    tableViewType ?? TABLE_VIEW_TYPE.UNKNOWN,
  );
  const { restoreInitialTableView, loadingInitialTableView } = useInitialTableView(
    tableViewType ?? TABLE_VIEW_TYPE.UNKNOWN,
  );

  const onGridReady = useCallback(
    async (gre: GridReadyEvent<TData>) => {
      if (isNotNil(detailTables)) {
        // Intermediate table renderer params
        const detailCellRendererParams = {
          detailGridOptions: {
            ...baseGridDef,
            rowHeight,
            headerHeight: 0,
            domLayout: 'autoHeight',
            masterDetail: true,
            defaultColDef: {
              ...baseColumnDef,
              suppressHeaderContextMenu: true,
              contextMenuItems: [],
              enableCellChangeFlash: false,
              sortable: false,
              resizable: false,
            },
            getRowId: (d) => d.data.title,
            columnDefs: [
              ...(extraSpace - 48 > 0
                ? [
                    {
                      width: extraSpace - 48,
                      resizable: false,
                      sortable: false,
                      lockPosition: 'left',
                      valueFormatter: () => '',
                    },
                  ]
                : []),
              {
                cellRenderer: 'agGroupCellRenderer',
                width: 58,
                lockPosition: 'left',
              },
              {
                field: 'title',
                width: 200,
                cellRenderer: ({ value, data }: ICellRendererParams<MidLevelRow<TData, DetailTitle>>) => (
                  <Flex align="center" gap="xs">
                    <EvolveIcon icon={data?.icon ?? null} light />
                    {value}
                  </Flex>
                ),
              },
              {
                width: 160,
                cellRenderer: CellRenderer<MidLevelRow<TData, DetailTitle>>(
                  ({ data, api, node }) => {
                    const detailConfig = detailTables.find((t) => t.title === data.title);
                    if (isNil(data.parentRowData) || isNil(detailConfig) || !detailConfig.exportable) return null;
                    return (
                      <ExportPaginatedGridButton
                        fileName={detailConfig.exportFileName?.(data.parentRowData) ?? detailConfig.title}
                        size="xs"
                        getGridApi={() => api.getDetailGridInfo(`detail_${detailConfig.title}`)?.api}
                        onClick={() => node.setExpanded(true)}
                        fullWidth
                      />
                    );
                  },
                  { flexProps: { justify: 'flex-end' } },
                ),
              },
              {
                minWidth: 64,
                flex: 1,
                cellRenderer: CellRenderer<MidLevelRow<TData, DetailTitle>>(
                  ({ data, api }) => {
                    const rightSide = detailTables.find((t) => t.title === data.title)?.rightSide;
                    if (isNil(rightSide) || isNil(data.parentRowData)) return null;
                    const refreshDetailGrid = (openIfClosed = false) => {
                      if (openIfClosed) {
                        api.getRowNode(data.title)?.setExpanded(true);
                      }
                      api.getDetailGridInfo(`detail_${data.title}`)?.api?.refreshServerSide({ purge: true });
                    };
                    return typeof rightSide === 'function'
                      ? rightSide(data.parentRowData, refreshDetailGrid)
                      : rightSide;
                  },
                  { flexProps: { justify: 'flex-end' } },
                ),
              },
            ],
            detailCellRenderer: DetailGridRenderer,
            detailCellRendererParams: (midLevelRow: ICellRendererParams<MidLevelRow<TData, DetailTitle>>) =>
              ({
                detailConfig: detailTables.find(({ title }) => title === midLevelRow.data?.title),
                rowHeight,
                extraSpace,
                refreshStrategy: 'everything',
              }) as Partial<
                IDetailCellRendererParams<
                  MidLevelRow<TData, DetailTitle>,
                  /**
                   * This type is the `DetailRowType` from `detailConfig`,
                   * but I'm not sure how to extract that (and it provides little/no value anyway)
                   */
                  unknown
                >
              >,
            onFirstDataRendered: (p) => {
              detailTables
                .filter((d) => d.autoExpand)
                .forEach(({ title }) => p.api.getRowNode(title)?.setExpanded(true));
            },
          },
          getDetailRowData: (p) => {
            p.successCallback(
              detailTables
                .filter((t) => !t.hidden)
                .map(({ title, icon }) => ({
                  title,
                  icon,
                  parentRowData: p.data,
                })),
            );
          },
        } as Partial<IDetailCellRendererParams<TData, MidLevelRow<TData, DetailTitle>>>;
        gre.api.setGridOption('detailCellRendererParams', detailCellRendererParams);
      }
      gre.api.setGridOption('loading', true);
      await restoreInitialTableView(gre.api);
      setDataSource(gre.api);
      setGridApi(gre.api);
    },
    [detailTables, extraSpace, rowHeight, setDataSource, restoreInitialTableView],
  );

  const expandDetailGrid = useCallback((parentRowId: TData[TKey], detailTableTitle: DetailTitle) => {
    gridRef.current?.api.getRowNode(parentRowId as string)?.setExpanded(true);
    // AG Grid needs time to render the intermediate detail grid
    // before you can expand the detail table
    setTimeout(() => {
      const detailGridId = `detail_${parentRowId as string}`;
      const detailGrid = gridRef.current?.api.getDetailGridInfo(detailGridId);
      detailGrid?.api?.getRowNode(detailTableTitle)?.setExpanded(true);
    }, 100);
  }, []);

  const refreshDetailGrid = useCallback((parentRowId: TData[TKey], detailTableTitle?: DetailTitle) => {
    const detailGridId = `detail_${parentRowId as string}`;
    const detailGrid = gridRef.current?.api.getDetailGridInfo(detailGridId);
    if (isNotNil(detailTableTitle)) {
      const childDetailGrid = detailGrid?.api?.getDetailGridInfo(`detail_${detailTableTitle}`);
      childDetailGrid?.api?.refreshServerSide({ purge: true });
    } else {
      detailGrid?.api?.forEachDetailGridInfo((grid) => grid.api?.refreshServerSide({ purge: true }));
    }
  }, []);

  const selectNextRow = useCallback(
    (
      index: number,
      /** If false, will select the previous row instead of the next row */
      next = true,
    ) => {
      const numRows = gridRef.current?.api.getDisplayedRowCount() ?? 0;
      if (numRows > 0) {
        let newIndex = index + (next ? 1 : -1);
        if (newIndex < 0) newIndex += numRows;
        else if (newIndex >= numRows) newIndex -= numRows;
        const newRow = gridRef.current?.api.getDisplayedRowAtIndex(newIndex);
        const data = newRow?.data as PotentialRowType | undefined;
        if (newRow?.detail || data?.__type === 'group' || data?.__type === 'no-data') {
          // oxlint-disable-next-line react/immutability
          selectNextRow(newIndex, next);
        } else {
          newRow?.setSelected(true, true);
          gridRef.current?.api.ensureIndexVisible(newIndex);
        }
      }
    },
    [],
  );

  /**
   * AG Grid props for server-sided grouping.
   * Unfortunately, AG Grid's typing is *very* suspect here,
   * so we have to do a lot of manual typing and casting.
   */
  const groupingProps = useMemo<Partial<AgGridReactProps> | undefined>(
    () =>
      grouping
        ? ({
            treeData: true,
            groupDisplayType: 'groupRows',
            isServerSideGroup: (d: PotentialRowType | undefined) => d?.__type === 'group',
            isFullWidthRow: (p) => p.rowNode.data?.__type === 'group' || p.rowNode.data?.__type === 'no-data',
            getServerSideGroupKey: (d: GData | undefined) => d?.[grouping.rowId],
            // Master rows are top-level TData rows
            isRowMaster: (d) => d.__type !== 'group' && d.__type !== 'no-data',
            fullWidthCellRenderer: ({ data, api }: { data: PotentialRowType | undefined; api: GridApi }) => {
              const padding = grouping.padding ?? 64;
              if (data?.__type === 'group') {
                return <GroupRowRenderer api={api} data={data} padding={padding} {...grouping} />;
              }
              if (data?.__type === 'no-data') {
                return <NoRowsOverlay pl={padding} pt={6} align="flex-start" label="No results found." />;
              }
              return undefined;
            },
            getRowId: (row) => {
              if (row.data.__type === 'no-data') return row.data.id;
              if (row.level === 0) {
                return (row as GetRowIdParams<GData>).data[grouping.rowId];
              }
              const data = (row as GetRowIdParams<TData>).data;
              return typeof rowId === 'function' ? rowId(data) : data[rowId];
            },
            getRowStyle: (params) =>
              params.data?.__type === 'group'
                ? {
                    backgroundColor: 'var(--ag-header-background-color)',
                  }
                : undefined,
          } as AgGridReactProps<PotentialRowType>)
        : undefined,
    [grouping, rowId],
  );

  const canBeFiltered = useMemo(() => colDef.some((column) => 'filter' in column && isNotNil(column.filter)), [colDef]);
  const sideBar = useSideBar({
    gridApi,
    tableName,
    tableViewType,
    loadingInitialTableView,
    selectedTableViewModified,
    showFiltersPanel: canBeFiltered,
    exportFileName,
  });

  const clearAllSortsMenuItem = useMemo<MenuItemDef>(
    () => ({
      name: 'Clear All Sorts',
      action: () => {
        if (!gridApi) return;
        const columns = gridApi.getColumns() ?? [];
        gridApi.applyColumnState({ state: columns.map((col) => ({ colId: col.getId(), sort: null })) });
      },
      icon: `<img alt="clear-all-sorts-icon" style="height: 16px; display: flex;" src="data:image/svg+xml;utf8,${encodeURIComponent(SortIcon)}" />`,
    }),
    [gridApi],
  );

  const { apiCall: getTableViewApiCall, loading: loadingTableView } = useEvolveGet('admin/tableView/:tableViewId');
  const resetTableViewMenuItem = useMemo<MenuItemDef>(
    () => ({
      name: 'Reset Table View',
      disabled: loadingTableView,
      action: async () => {
        if (!gridApi) return;
        if (selectedTableViewId) {
          const tableView = await getTableViewApiCall({ url: `admin/tableView/${selectedTableViewId}` });
          recordAppliedTableView(selectedTableViewId, tableView.gridState);
          loadSavedGridState(tableView.gridState, gridApi, true);
        } else {
          // Reset to system default
          gridApi.resetColumnState();
          gridApi.setFilterModel(null);
        }
      },
      icon: `<img alt="reset-table-view-icon" style="height: 16px; display: flex;" src="data:image/svg+xml;utf8,${encodeURIComponent(ResetIcon)}" />`,
    }),
    [gridApi, selectedTableViewId, getTableViewApiCall, loadingTableView, recordAppliedTableView],
  );

  const tableHasSort = useCallback((api: GridApi) => {
    const columns = api.getColumns() ?? [];
    return columns.some((column) => column.getSort());
  }, []);

  // Locked columns have a restricted set of menu options.
  const getLockedColumnMainMenuItems = useCallback(
    ({ api }: GetMainMenuItemsParams) => {
      const updatedMenuItems: (DefaultMenuItem | MenuItemDef<TData>)[] = [];
      if (tableHasSort(api)) {
        updatedMenuItems.push(clearAllSortsMenuItem, 'separator');
      }
      updatedMenuItems.push(menuOverrides['autoSizeAll']);
      if (tableViewType) {
        updatedMenuItems.push('separator', resetTableViewMenuItem);
      }
      return updatedMenuItems;
    },
    [tableViewType, tableHasSort, clearAllSortsMenuItem, resetTableViewMenuItem],
  );

  const getColumnMainMenuItems = useCallback(
    ({ api, defaultItems }: GetMainMenuItemsParams) => {
      // Use our custom overrides for certain default menu items, and keep the rest of the default items.
      const updatedMenuItems: (DefaultMenuItem | MenuItemDef<TData>)[] = defaultItems.map((item) =>
        isKeyOf(item, menuOverrides) ? menuOverrides[item] : item,
      );
      if (tableHasSort(api)) {
        // Insert "Clear Sorts" menu option after the existing "Clear Sort" option (if the column has a sort) or after the "Sort Descending" option (if it exists).
        const sortUnSortIndex = updatedMenuItems.indexOf('sortUnSort');
        const sortDescendingIndex = updatedMenuItems.indexOf('sortDescending');
        const insertIndex =
          sortUnSortIndex !== -1 ? sortUnSortIndex + 1 : sortDescendingIndex !== -1 ? sortDescendingIndex + 1 : -1;
        if (insertIndex !== -1) {
          updatedMenuItems.splice(insertIndex, 0, clearAllSortsMenuItem);
        } else {
          updatedMenuItems.unshift(clearAllSortsMenuItem, 'separator');
        }
      }
      if (tableViewType) {
        // Remove the "Reset Columns" menu option if it exists, since we have our own "Reset Table View" option.
        const resetColumnsIndex = updatedMenuItems.indexOf('resetColumns');
        if (resetColumnsIndex !== -1) {
          updatedMenuItems.splice(resetColumnsIndex, 1);
        }
        // Insert "Reset Table View" menu option at the bottom of the menu.
        updatedMenuItems.push('separator');
        updatedMenuItems.push(resetTableViewMenuItem);
      }
      return updatedMenuItems;
    },
    [tableViewType, tableHasSort, clearAllSortsMenuItem, resetTableViewMenuItem],
  );

  const spreadableAgGridProps = useMemo<
    // For all intents and purposes, we want all users of `useServerSideGrid`
    // to treat the grid as if it only contains TData.
    AgGridReactProps<TData> & {
      ref: typeof gridRef;
    }
  >(
    () => ({
      ...baseGridDef,
      ref: gridRef,
      onGridReady,
      columnDefs: colDef,
      getRowId: (row: GetRowIdParams<TData>) =>
        typeof rowId === 'function' ? rowId(row.data) : (row.data[rowId] as string),
      masterDetail,
      rowModelType: 'serverSide',
      rowHeight,
      headerHeight: rowHeight,
      sideBar: showSidebar ? sideBar : undefined,
      context,
      getMainMenuItems: (params) => {
        const isLockedColumn = params.column?.getColId().startsWith(doNotSaveColumnIdentifier);
        return isLockedColumn ? getLockedColumnMainMenuItems(params) : getColumnMainMenuItems(params);
      },
      ...groupingProps,
    }),
    [
      onGridReady,
      colDef,
      sideBar,
      showSidebar,
      masterDetail,
      rowHeight,
      groupingProps,
      context,
      rowId,
      getLockedColumnMainMenuItems,
      getColumnMainMenuItems,
    ],
  );

  useEffect(() => {
    if (!gridApi) return undefined;

    // When the user hides a column, remove any active filters or sorts on that column.
    const onChangeColumnVisibility = ({ column, visible }: ColumnVisibleEvent) => {
      if (visible) return;
      if (column?.isFilterActive()) {
        gridApi.destroyFilter(column);
      }
      if (column?.getSort()) {
        gridApi.applyColumnState({ state: [{ colId: column.getId(), sort: null }] });
      }
    };
    gridApi.addEventListener('columnVisible', onChangeColumnVisibility);
    return () => {
      if (gridApi.isDestroyed()) return;
      gridApi.removeEventListener('columnVisible', onChangeColumnVisibility);
    };
  }, [gridApi]);

  useEffect(() => {
    if (!gridApi || isNil(tableViewType)) return undefined;

    // Keep the user's unsaved changes for the rest of their session.
    const onStateUpdated = ({ sources, state }: StateUpdatedEvent) => {
      const savedStateChanged = sources.some(
        (source) => source === 'api' || SAVED_GRID_STATE_KEYS.some((savedGridStateKey) => savedGridStateKey === source),
      );
      if (!savedStateChanged) return;
      recordGridState(stringifyGridStateJson(state));
    };
    gridApi.addEventListener('stateUpdated', onStateUpdated);
    return () => {
      if (gridApi.isDestroyed()) return;
      gridApi.removeEventListener('stateUpdated', onStateUpdated);
    };
  }, [gridApi, tableViewType, recordGridState]);

  return useMemo(
    () =>
      ({
        /**
         * Spread these props onto your `AgGridReact` component. Example:
         *
         *     const { agGridProps } = useServerSideGrid({...});
         *     return <AgGridReact {...agGridProps} />;
         * */
        agGridProps: spreadableAgGridProps,
        gridApi,
        refreshDetailGrid,
        expandDetailGrid,
        selectNextRow,
        totalRowCount,
      }) as const,
    [spreadableAgGridProps, gridApi, refreshDetailGrid, expandDetailGrid, selectNextRow, totalRowCount],
  );
};
